import React from 'react';
import { View, Text, Image, Modal, TouchableOpacity, ScrollView, StyleSheet, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useThemeColors } from '../constants/theme';

/**
 * Ficha que se despliega al tocar la foto de la otra persona en un viaje.
 *
 * POR QUÉ EXISTE
 * El círculo de la foto era un `<View>` pelado en las tres tarjetas
 * (ActiveRideCard ×2 y RideOfferCard): sin `onPress`, sin `Touchable`. El
 * fundador lo tocaba esperando ver los datos y no pasaba nada — y en todo el
 * proyecto no existía ninguna pantalla de ficha que abrir.
 *
 * Es lo que hacen Uber e InDriver: tocas la foto y ves con quién vas y en qué
 * carro. Para el pasajero es lo que le deja comparar la placa con el carro que
 * tiene delante antes de montarse.
 *
 * No pide nada al servidor: pinta los datos que la tarjeta ya tiene. Si algún
 * dato falta, se dice — nunca se inventa ni se deja un hueco mudo.
 */

export type DatosFicha = {
  /** 'DRIVER' pinta los datos del vehículo; 'PASSENGER' los omite. */
  tipo: 'DRIVER' | 'PASSENGER';
  nombre?: string | null;
  foto?: string | null;
  /** Promedio real. Si no hay, se muestra "Sin calificaciones aún". */
  calificacion?: string | number | null;
  /** Cuántas calificaciones respaldan el promedio. 0 o null = usuario nuevo. */
  numeroCalificaciones?: number | null;
  verificado?: boolean;
  marca?: string | null;
  modelo?: string | null;
  color?: string | null;
  placa?: string | null;
  /**
   * La foto del vehículo, la MISMA que estampó el servidor en la oferta
   * (`vehiculo_publico/`, functions/src/fotoVehiculo.ts). Se enseña grande
   * aquí porque es donde se lee la placa: el pasajero compara ese número con
   * el carro que tiene delante ANTES de montarse, no después.
   */
  fotoVehiculo?: string | null;
  /** Texto ya compuesto del vehículo, si la tarjeta solo tiene eso. */
  vehiculo?: string | null;
  /**
   * EL RANGO DEL CHOFER: 'BRONCE' | 'PLATA' | 'ORO' | 'PLATINO' | 'DIAMANTE'.
   * Lo calcula el SERVIDOR con los viajes cumplidos y la nota de los pasajeros
   * (functions/src/niveles.ts) y viaja estampado en la oferta; el chofer no
   * puede escribirlo. Si no viene, no se pinta nada — un rango inventado en el
   * cliente valdría menos que ninguno.
   */
  nivel?: string | null;
  /** Viajes cumplidos, para que el rango no sea una palabra suelta. */
  viajesCompletados?: number | null;
};

/**
 * CÓMO SE VE CADA RANGO.
 *
 * Los colores son los del metal, que es lo que la gente ya reconoce sin que
 * nadie se lo explique. No hay icono: una medalla al lado de las estrellas
 * compite con ellas, y la nota sigue siendo el dato que manda.
 */
const PINTA_DEL_NIVEL: Record<string, { texto: string; color: string; fondo: string }> = {
  BRONCE: { texto: 'Bronce', color: '#8A5A2B', fondo: 'rgba(205,127,50,0.16)' },
  PLATA: { texto: 'Plata', color: '#5B6472', fondo: 'rgba(148,163,184,0.22)' },
  ORO: { texto: 'Oro', color: '#9A6B00', fondo: 'rgba(245,158,11,0.20)' },
  PLATINO: { texto: 'Platino', color: '#3F5C63', fondo: 'rgba(94,158,170,0.20)' },
  DIAMANTE: { texto: 'Diamante', color: '#1D5E8C', fondo: 'rgba(56,154,214,0.20)' },
};

type Props = {
  visible: boolean;
  onCerrar: () => void;
  datos: DatosFicha;
};

/**
 * LA FOTO DEL VEHICULO, COMPLETA.
 *
 * Iba con `resizeMode="cover"` dentro de una caja de alto FIJO (170 px): la
 * imagen se agranda hasta tapar la caja entera y lo que sobra se recorta. Un
 * carro se fotografia casi siempre con el telefono de pie, asi que lo que
 * sobraba era arriba y abajo — y abajo es donde esta la placa. Justo el unico
 * dato por el que esta foto existe: comparar el numero con el carro que se
 * tiene delante. El comentario de su propio JSX lo decia y el dibujado hacia lo
 * contrario.
 *
 * Ahora la caja toma la forma de la foto (`aspectRatio` leido de la imagen real)
 * y se dibuja con `contain`: no se recorta nada. La proporcion se limita para
 * que una foto muy vertical no se coma la ficha entera.
 */
function FotoDelVehiculo({ uri }: { uri: string }) {
  // 4/3 mientras se mide. Es la proporcion mas comun y evita que la caja nazca
  // plana y de un salto al llegar el tamano real.
  const [proporcion, setProporcion] = React.useState(4 / 3);

  React.useEffect(() => {
    let vivo = true;
    Image.getSize(
      uri,
      (ancho, alto) => {
        if (!vivo || !ancho || !alto) return;
        // Entre 3:4 (vertical) y 16:9 (panoramica). Fuera de ese rango la foto
        // mandaria sobre el tamano de la ficha en vez de al contrario.
        setProporcion(Math.min(16 / 9, Math.max(0.75, ancho / alto)));
      },
      // Si no se puede medir se queda en 4/3 con `contain`: se vera la foto
      // entera con franjas, que es peor de aspecto pero nunca esconde la placa.
      () => {},
    );
    return () => {
      vivo = false;
    };
  }, [uri]);

  return (
    <Image
      source={{ uri }}
      style={[styles.fotoVehiculo, { aspectRatio: proporcion }]}
      resizeMode="contain"
      accessibilityLabel="Foto del vehículo del chofer, con su placa"
    />
  );
}

export default function FichaPersona({ visible, onCerrar, datos }: Props) {
  const colors = useThemeColors();
  const esChofer = datos.tipo === 'DRIVER';
  // `maxHeight: '80%'` resolvía contra un padre de altura AUTO y recortaba el
  // ~20% inferior de la tarjeta — en las capturas del fundador la ficha del
  // chofer perdía justo lo de debajo de "VEHÍCULO" y la del pasajero perdía
  // el papel y las estrellas. Con píxeles de la ventana no hay ambigüedad.
  const { height: altoVentana } = useWindowDimensions();

  /** Limpia un dato de texto: un espacio en blanco NO es un dato. */
  const limpio = (v: string | null | undefined) => String(v ?? '').trim();

  const descripcionVehiculo =
    [datos.color, datos.marca, datos.modelo].map(limpio).filter(Boolean).join(' ') ||
    limpio(datos.vehiculo);
  const placaLimpia = limpio(datos.placa);

  const n = Number(datos.numeroCalificaciones ?? 0);
  const promedio = datos.calificacion != null ? String(datos.calificacion) : null;
  // Sin respaldo de votos, un "5.0" es una promesa vacía. Se dice la verdad.
  const textoCalificacion =
    n > 0 && promedio
      ? `${promedio} · ${n} ${n === 1 ? 'calificación' : 'calificaciones'}`
      : 'Sin calificaciones aún';

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onCerrar}>
      <TouchableOpacity style={styles.fondo} activeOpacity={1} onPress={onCerrar}>
        {/* El TouchableOpacity interior traga el toque para que tocar DENTRO de
            la ficha no la cierre sin querer. */}
        <TouchableOpacity activeOpacity={1} style={{ width: '100%', alignItems: 'center' }}>
          <View style={[styles.caja, { backgroundColor: colors.card, maxHeight: altoVentana * 0.8 }]}>
            <TouchableOpacity style={styles.cerrar} onPress={onCerrar} accessibilityRole="button">
              <Ionicons name="close" size={24} color={colors.textSecondary} />
            </TouchableOpacity>

            <ScrollView showsVerticalScrollIndicator contentContainerStyle={{ alignItems: 'center' }}>
              <View style={styles.avatarMarco}>
                {datos.foto ? (
                  <Image source={{ uri: datos.foto }} style={styles.avatar} resizeMode="cover" />
                ) : (
                  <View style={[styles.avatar, styles.avatarVacio, { backgroundColor: colors.background }]}>
                    <Ionicons name="person" size={44} color={colors.textSecondary} />
                  </View>
                )}
                {datos.verificado && (
                  <View style={styles.sello}>
                    <Ionicons name="checkmark-circle" size={26} color="#22C55E" />
                  </View>
                )}
              </View>

              <Text style={[styles.nombre, { color: colors.text }]}>
                {datos.nombre?.trim() || (esChofer ? 'Chofer' : 'Pasajero')}
              </Text>
              <Text style={[styles.papel, { color: colors.textSecondary }]}>
                {esChofer ? 'Chofer' : 'Pasajero'}
                {datos.verificado ? ' · Identidad verificada' : ''}
              </Text>

              <View style={styles.estrellas}>
                <Ionicons name="star" size={16} color={n > 0 ? '#F59E0B' : colors.textSecondary} />
                <Text style={[styles.calificacion, { color: colors.textSecondary }]}>
                  {textoCalificacion}
                </Text>
              </View>

              {/* EL RANGO, debajo de la nota y no al lado: la nota es el dato
                  que decide, el rango acompaña. Solo para choferes — un
                  pasajero no tiene rango. */}
              {esChofer && !!datos.nivel && !!PINTA_DEL_NIVEL[String(datos.nivel)] && (
                <View
                  style={[
                    styles.nivelMarco,
                    { backgroundColor: PINTA_DEL_NIVEL[String(datos.nivel)].fondo },
                  ]}
                >
                  <Text
                    style={[
                      styles.nivelTexto,
                      { color: PINTA_DEL_NIVEL[String(datos.nivel)].color },
                    ]}
                  >
                    Chofer {PINTA_DEL_NIVEL[String(datos.nivel)].texto}
                    {typeof datos.viajesCompletados === 'number' && datos.viajesCompletados > 0
                      ? ` · ${datos.viajesCompletados} viajes`
                      : ''}
                  </Text>
                </View>
              )}

              {esChofer && (
                <View style={[styles.bloqueVehiculo, { borderColor: colors.border }]}>
                  <Text style={[styles.tituloBloque, { color: colors.textSecondary }]}>VEHÍCULO</Text>

                  {/* LA FOTO DEL CARRO, GRANDE. Es donde se lee la placa, y es
                      lo que el pasajero quiere ver ANTES de aceptar: comparar
                      el número con el carro que tiene delante. La estampa el
                      servidor (vehiculo_publico/), nunca la manda el teléfono
                      del chofer. */}
                  {datos.fotoVehiculo ? (
                    <FotoDelVehiculo uri={String(datos.fotoVehiculo)} />
                  ) : null}

                  {descripcionVehiculo ? (
                    <Text style={[styles.vehiculo, { color: colors.text }]}>{descripcionVehiculo}</Text>
                  ) : (
                    <Text style={[styles.faltante, { color: colors.textSecondary }]}>
                      Sin datos del vehículo
                    </Text>
                  )}

                  {placaLimpia ? (
                    <View style={styles.placaMarco}>
                      <Text style={styles.placaTexto}>{placaLimpia}</Text>
                    </View>
                  ) : (
                    <Text style={[styles.faltante, { color: colors.textSecondary }]}>
                      Placa no informada
                    </Text>
                  )}

                  <Text style={[styles.consejo, { color: colors.textSecondary }]}>
                    Compara la placa con el carro antes de montarte.
                  </Text>
                </View>
              )}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
}

const styles = StyleSheet.create({
  fondo: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.55)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  caja: { width: '100%', maxWidth: 380, borderRadius: 22, padding: 22 },
  cerrar: { position: 'absolute', top: 10, right: 10, padding: 8, zIndex: 2 },
  avatarMarco: { marginTop: 6, marginBottom: 12 },
  avatar: { width: 104, height: 104, borderRadius: 52 },
  avatarVacio: { alignItems: 'center', justifyContent: 'center' },
  sello: { position: 'absolute', bottom: 0, right: 0, backgroundColor: '#FFF', borderRadius: 13 },
  nombre: { fontSize: 21, fontWeight: 'bold', textAlign: 'center' },
  papel: { fontSize: 13, marginTop: 3, textAlign: 'center' },
  estrellas: { flexDirection: 'row', alignItems: 'center', marginTop: 10, gap: 6 },
  calificacion: { fontSize: 13 },
  bloqueVehiculo: {
    width: '100%',
    marginTop: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    alignItems: 'center',
  },
  tituloBloque: { fontSize: 11, fontWeight: 'bold', letterSpacing: 1, marginBottom: 8 },
  vehiculo: { fontSize: 17, fontWeight: '600', textTransform: 'capitalize', textAlign: 'center' },
  faltante: { fontSize: 13, fontStyle: 'italic', marginTop: 4 },
  placaMarco: {
    marginTop: 10,
    paddingHorizontal: 16,
    paddingVertical: 7,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#1F2937',
    backgroundColor: '#FFFFFF',
  },
  fotoVehiculo: {
    width: '100%',
    // Sin alto fijo: lo pone `aspectRatio` en FotoDelVehiculo, que es lo que
    // evita el recorte. Un maximo para que una foto vertical no empuje la placa
    // y el aviso fuera de la ficha.
    maxHeight: 280,
    borderRadius: 12,
    marginBottom: 10,
    // Algo mas oscuro que antes: si la medida falla y quedan franjas, se leen
    // como el marco de la foto y no como un hueco de la pantalla.
    backgroundColor: 'rgba(17,24,39,0.10)',
  },
  placaTexto: { fontSize: 20, fontWeight: '900', letterSpacing: 2.5, color: '#111827' },
  nivelMarco: {
    alignSelf: 'center',
    marginTop: 8,
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: 12,
  },
  nivelTexto: { fontSize: 12.5, fontWeight: '800' },
  consejo: { fontSize: 11.5, marginTop: 12, textAlign: 'center', lineHeight: 16 },
});
